import React, { useState, useEffect } from 'react';
import { API } from '../utils/constants';
import { todayISO } from '../utils/helpers';
import PatternLock from './PatternLock';

const QuickLossModal = ({ isOpen, onClose, location }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [kitchenPattern, setKitchenPattern] = useState(null);
  const [loadingPattern, setLoadingPattern] = useState(true);
  
  // Form states
  const [lossDate, setLossDate] = useState(todayISO());
  const [selectedItems, setSelectedItems] = useState([]);
  const [lossCategory, setLossCategory] = useState('Straty kuchenne');
  
  // Product selection
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedProductForQuantity, setSelectedProductForQuantity] = useState(null);
  const [quantityInput, setQuantityInput] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('kg');
  
  // Add Product Modal
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductUnit, setNewProductUnit] = useState('kg');
  const [newProductCategory, setNewProductCategory] = useState('');
  
  const units = ['kg', 'szt', 'l', 'g', 'ml'];
  const categories = ['Warzywa', 'Owoce', 'Nabiał', 'Mięso', 'Ryby', 'Przyprawy', 'Inne'];

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      fetchKitchenPattern();
      setSuccess(false);
      setIsUnlocked(false);
    }
  }, [isOpen, location]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchKitchenPattern = async () => {
    try {
      setLoadingPattern(true);
      const response = await fetch(`${API}/settings/kitchen-pattern?location=${location}`);
      const data = await response.json();
      setKitchenPattern(data.pattern);
    } catch (error) {
      console.error('Error fetching kitchen pattern:', error);
      setKitchenPattern(null);
    } finally {
      setLoadingPattern(false);
    }
  };

  const handlePatternComplete = (pattern) => {
    setIsUnlocked(true);
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/products?location=${location}`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Błąd pobierania produktów:', error);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProductForQuantity(product);
    setSelectedUnit(product.unit);
    setQuantityInput('');
    setShowQuantityModal(true);
    setShowSuggestions(false);
    setSearchTerm('');
  };

  const handleAddQuantity = () => {
    if (!quantityInput || parseFloat(quantityInput) <= 0) {
      alert('Wpisz prawidłową ilość');
      return;
    }

    const newItem = {
      _uniqueKey: `${selectedProductForQuantity.id}-${Date.now()}`,
      productId: selectedProductForQuantity.id,
      productName: selectedProductForQuantity.name,
      quantity: parseFloat(quantityInput),
      unit: selectedUnit,
      category: selectedProductForQuantity.category
    };

    setSelectedItems(prev => [...prev, newItem]);
    setShowQuantityModal(false);
    setQuantityInput('');
  };

  const handleAddProduct = async () => {
    if (!newProductName.trim()) {
      alert('Wpisz nazwę produktu');
      return;
    }
    if (!newProductCategory) {
      alert('Wybierz kategorię');
      return;
    }

    try {
      const response = await fetch(`${API}/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newProductName,
          unit: newProductUnit,
          category: newProductCategory,
          location
        })
      });

      if (response.ok) {
        const newProduct = await response.json();
        setProducts(prev => [...prev, newProduct]);
        alert('✅ Produkt dodany!');
        setNewProductName('');
        setNewProductUnit('kg');
        setNewProductCategory('');
        setShowAddProductModal(false);
      } else {
        const error = await response.json();
        alert(`❌ ${error.detail}`);
      }
    } catch (error) {
      console.error('Błąd dodawania produktu:', error);
      alert('❌ Wystąpił błąd');
    }
  };

  const handleRemoveItem = (uniqueKey) => {
    setSelectedItems(prev => prev.filter(item => item._uniqueKey !== uniqueKey));
  };

  const handleSaveLoss = async () => {
    if (selectedItems.length === 0) {
      alert('Dodaj przynajmniej jeden produkt');
      return;
    }

    setLoading(true);
    try {
      const itemsToSend = selectedItems.map(({ _uniqueKey, ...item }) => item);

      const response = await fetch(`${API}/kitchen-losses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: lossDate,
          cookId: 'kitchen-quick',
          cookName: 'Kuchnia',
          items: itemsToSend,
          category: lossCategory,
          notes: '',
          location
        })
      });

      if (response.ok) {
        setSuccess(true);
        // Auto-close after 2 seconds
        setTimeout(() => {
          handleClose();
        }, 2500);
      } else {
        const error = await response.json();
        alert(`❌ ${error.detail}`);
      }
    } catch (error) {
      console.error('Błąd zapisywania:', error);
      alert('❌ Wystąpił błąd');
    }
    setLoading(false);
  };

  const handleClose = () => {
    setSelectedItems([]);
    setSuccess(false);
    onClose();
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  // Show pattern lock if pattern exists and not unlocked
  if (kitchenPattern && !isUnlocked && !success && !loadingPattern) {
    return (
      <PatternLock
        mode="verify"
        currentPattern={kitchenPattern}
        onComplete={handlePatternComplete}
        onCancel={onClose}
      />
    );
  }

  // Success screen
  if (success) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}
        onClick={handleClose}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: '20px',
            maxWidth: '500px',
            width: '100%',
            padding: '40px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            border: '2px solid rgba(34, 197, 94, 0.3)',
            textAlign: 'center'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ fontSize: '72px', marginBottom: '20px' }}>✅</div>
          <h2 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '28px', 
            fontWeight: 'bold', 
            color: '#22c55e'
          }}>
            Strata zapisana!
          </h2>
          <div style={{ 
            fontSize: '16px', 
            color: 'rgba(255,255,255,0.8)',
            marginBottom: '12px'
          }}>
            Dodano {selectedItems.length} {selectedItems.length === 1 ? 'produkt' : 'produktów'}
          </div>
          <div style={{
            padding: '16px',
            background: 'rgba(34, 197, 94, 0.1)',
            borderRadius: '12px',
            fontSize: '14px',
            color: 'rgba(255,255,255,0.7)',
            marginTop: '20px'
          }}>
            Zgłoszono przez: <strong style={{ color: '#22c55e' }}>Kuchnia</strong>
          </div>
          <div style={{ 
            marginTop: '24px', 
            fontSize: '13px', 
            color: 'rgba(255,255,255,0.5)'
          }}>
            Automatyczne zamknięcie...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
        overflowY: 'auto'
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          borderRadius: isMobile ? '12px' : '20px',
          maxWidth: isMobile ? '100%' : '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          border: '2px solid rgba(239, 68, 68, 0.3)',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: isMobile ? '16px 20px' : '24px 32px',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '24px', fontWeight: 'bold', color: 'white' }}>
              🔥 Szybkie dodanie straty
            </h2>
            <p style={{ margin: '4px 0 0 0', color: 'rgba(255, 255, 255, 0.9)', fontSize: isMobile ? '12px' : '14px' }}>
              Zgłoszenie z kuchni • {location === 'krakow' ? 'Kraków' : 'Nowy Sącz'}
            </p>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '8px',
              width: '36px',
              height: '36px',
              cursor: 'pointer',
              fontSize: '20px',
              color: 'white',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: isMobile ? '16px 20px' : '24px 32px'
        }}>
          {/* Category Selector */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              fontWeight: '600',
              color: 'white',
              fontSize: '15px'
            }}>
              Kategoria
            </label>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {['Straty kuchenne', 'Produkty wydane', 'Cateringi'].map(cat => (
                <button
                  key={cat}
                  onClick={() => setLossCategory(cat)}
                  style={{
                    flex: isMobile ? '1 1 100%' : '1 1 180px',
                    padding: '12px 16px',
                    background: lossCategory === cat 
                      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
                      : 'rgba(255,255,255,0.05)',
                    border: lossCategory === cat ? '2px solid #6366f1' : '2px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {cat === 'Straty kuchenne' && '🔪'} 
                  {cat === 'Produkty wydane' && '📦'} 
                  {cat === 'Cateringi' && '🎉'} {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Product Search */}
          <div style={{ marginBottom: '24px', position: 'relative' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <label style={{ 
                fontWeight: '600',
                color: 'white',
                fontSize: '15px'
              }}>
                Wyszukaj produkt
              </label>
              <button
                onClick={() => setShowAddProductModal(true)}
                style={{
                  padding: '6px 12px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <span>➕</span>
                <span>Nowy produkt</span>
              </button>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Wpisz nazwę produktu..."
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'rgba(255,255,255,0.05)',
                border: '2px solid rgba(255,255,255,0.1)',
                borderRadius: '10px',
                color: 'white',
                fontSize: '15px'
              }}
            />
            
            {/* Suggestions */}
            {showSuggestions && searchTerm && filteredProducts.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '8px',
                background: '#1e293b',
                border: '2px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                maxHeight: '300px',
                overflowY: 'auto',
                zIndex: 100,
                boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
              }}>
                {filteredProducts.slice(0, 10).map(product => (
                  <div
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ color: 'white', fontSize: '15px', fontWeight: '600' }}>
                      {product.name}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginTop: '2px' }}>
                      {product.category} • {product.unit}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Items */}
          {selectedItems.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '12px', 
                fontWeight: '600',
                color: 'white',
                fontSize: '15px'
              }}>
                Wybrane produkty ({selectedItems.length})
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {selectedItems.map(item => (
                  <div
                    key={item._uniqueKey}
                    style={{
                      padding: '12px 16px',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ color: 'white', fontSize: '15px', fontWeight: '600' }}>
                        {item.productName}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginTop: '2px' }}>
                        {item.quantity} {item.unit} • {item.category}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(item._uniqueKey)}
                      style={{
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: '1px solid rgba(239, 68, 68, 0.4)',
                        borderRadius: '8px',
                        width: '32px',
                        height: '32px',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#ef4444',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.3)'}
                      onMouseLeave={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.2)'}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: isMobile ? '16px 20px' : '20px 32px',
          borderTop: '2px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleClose}
            style={{
              padding: '12px 24px',
              background: 'rgba(255,255,255,0.1)',
              border: '2px solid rgba(255,255,255,0.2)',
              borderRadius: '10px',
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
          >
            Anuluj
          </button>
          <button
            onClick={handleSaveLoss}
            disabled={loading || selectedItems.length === 0}
            style={{
              padding: '12px 32px',
              background: selectedItems.length === 0 
                ? 'rgba(255,255,255,0.1)' 
                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontSize: '15px',
              fontWeight: '700',
              cursor: selectedItems.length === 0 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Zapisywanie...' : '✅ Zapisz stratę'}
          </button>
        </div>

        {/* Quantity Modal */}
        {showQuantityModal && selectedProductForQuantity && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 200
            }}
            onClick={() => setShowQuantityModal(false)}
          >
            <div
              style={{
                background: '#1e293b',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '400px',
                width: '90%',
                border: '2px solid rgba(99, 102, 241, 0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ 
                margin: '0 0 16px 0', 
                color: 'white',
                fontSize: '20px'
              }}>
                {selectedProductForQuantity.name}
              </h3>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: '14px'
                }}>
                  Ilość
                </label>
                <input
                  type="number"
                  value={quantityInput}
                  onChange={(e) => setQuantityInput(e.target.value)}
                  placeholder="0"
                  step="0.01"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '2px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: '14px'
                }}>
                  Jednostka
                </label>
                <select
                  value={selectedUnit}
                  onChange={(e) => setSelectedUnit(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '2px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '15px'
                  }}
                >
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="szt">szt</option>
                  <option value="l">l</option>
                  <option value="ml">ml</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowQuantityModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Anuluj
                </button>
                <button
                  onClick={handleAddQuantity}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  Dodaj
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Add Product Modal */}
      {showAddProductModal && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 300,
            padding: '20px'
          }}
          onClick={() => setShowAddProductModal(false)}
        >
          <div
            style={{
              background: '#1e293b',
              borderRadius: '16px',
              padding: isMobile ? '24px' : '32px',
              maxWidth: '500px',
              width: '100%',
              border: '2px solid rgba(16, 185, 129, 0.3)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              margin: '0 0 24px 0',
              fontSize: isMobile ? '20px' : '22px',
              fontWeight: '700',
              color: '#10b981'
            }}>
              ➕ Nowy Produkt
            </h3>

            {/* Product Name */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: 'rgba(255,255,255,0.8)',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Nazwa produktu
              </label>
              <input
                type="text"
                value={newProductName}
                onChange={(e) => setNewProductName(e.target.value)}
                placeholder="np. Cebula"
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '2px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '15px'
                }}
              />
            </div>

            {/* Unit */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: 'rgba(255,255,255,0.8)',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Jednostka
              </label>
              <select
                value={newProductUnit}
                onChange={(e) => setNewProductUnit(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '2px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '15px'
                }}
              >
                {units.map(unit => (
                  <option key={unit} value={unit} style={{ background: '#1e293b', color: 'white' }}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: 'rgba(255,255,255,0.8)',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Kategoria
              </label>
              <select
                value={newProductCategory}
                onChange={(e) => setNewProductCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '2px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '15px'
                }}
              >
                <option value="" style={{ background: '#1e293b', color: 'white' }}>-- Wybierz --</option>
                {categories.map(cat => (
                  <option key={cat} value={cat} style={{ background: '#1e293b', color: 'white' }}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
              <button
                onClick={() => setShowAddProductModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '2px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Anuluj
              </button>
              <button
                onClick={handleAddProduct}
                style={{
                  flex: 2,
                  padding: '12px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                ✅ Dodaj produkt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickLossModal;
